/**
 * ShopShip Theme Scripts
 * Vanilla JS — lightweight, ultra-fluid 60fps animations.
 * Handles Shopify AJAX cart, hero slider, bundle builder, product gallery, and FAQ accordion.
 */

(function() {
  'use strict';

  const $ = (id) => document.getElementById(id);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  // Toast notification
  function showToast(message, duration = 2800) {
    let toast = $('theme-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'theme-toast';
      toast.className = 'theme-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, duration);
  }
  window.ShopShipToast = showToast;

  // Format money helper
  function formatMoney(cents) {
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.themeSettings?.moneyFormat || 'Rs. {{amount}}');
    }
    return 'Rs. ' + (Number(cents) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* ----------------------------------------------------
     1. Hero Carousel
     Slides sit in a native scroll-snap track (premium.css), so a swipe
     follows the finger with the browser's own momentum. JS only syncs the
     dots, autoplays while the hero is on screen and the shopper isn't
     touching it, and handles dots/keys/pause.
  ---------------------------------------------------- */
  function initHeroSliders() {
    $$('.hero-main').forEach((hero) => {
      if (hero.dataset.initialized === 'true') return;
      hero.dataset.initialized = 'true';
      const track = hero.querySelector('.hero-track');
      const slides = [...hero.querySelectorAll('.hero-slide')];
      const dotsContainer = hero.querySelector('.hero-dots');
      const pauseBtn = hero.querySelector('.hero-pause');
      if (!track || slides.length <= 1) {
        if (pauseBtn) pauseBtn.hidden = true;
        return;
      }

      const count = slides.length;
      const interval = Number(hero.dataset.slideInterval) || 6500;
      let current = 0;
      let timer = null;
      let onScreen = true;
      let touching = false;
      let isPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches || hero.dataset.autoplay === 'false' || Boolean(window.Shopify && Shopify.designMode);

      if (dotsContainer) {
        dotsContainer.innerHTML = '';
        slides.forEach((_, idx) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'hero-dot' + (idx === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', `Go to slide ${idx + 1}`);
          dot.addEventListener('click', () => { goToSlide(idx); restart(); });
          dotsContainer.appendChild(dot);
        });
      }

      function updateUI() {
        slides.forEach((slide, idx) => {
          slide.classList.toggle('is-active', idx === current);
          if (idx === current) slide.setAttribute('aria-current', 'true');
          else slide.removeAttribute('aria-current');
        });
        if (dotsContainer) {
          dotsContainer.querySelectorAll('.hero-dot').forEach((dot, idx) => {
            dot.classList.toggle('is-active', idx === current);
            dot.setAttribute('aria-current', idx === current ? 'true' : 'false');
          });
        }
      }
      function goToSlide(index) {
        current = (index + count) % count;
        track.scrollTo({ left: slides[current].offsetLeft - slides[0].offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
        updateUI();
      }
      function stop() { clearInterval(timer); timer = null; }
      function start() {
        stop();
        if (isPaused || !onScreen || document.hidden) return;
        timer = setInterval(() => { if (!touching) goToSlide(current + 1); }, interval);
      }
      function restart() { if (!isPaused) start(); }

      // The slide that is mostly in view is the current one (swipe or autoplay).
      if ('IntersectionObserver' in window) {
        const slideObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const idx = slides.indexOf(entry.target);
            if (idx !== current) { current = idx; updateUI(); }
          });
        }, { root: track, threshold: .6 });
        slides.forEach((slide) => slideObserver.observe(slide));
        new IntersectionObserver(([entry]) => {
          onScreen = entry.isIntersecting;
          if (onScreen) restart(); else stop();
        }).observe(hero);
      }

      track.addEventListener('pointerdown', () => { touching = true; stop(); }, { passive: true });
      const release = () => { if (!touching) return; touching = false; restart(); };
      track.addEventListener('pointerup', release, { passive: true });
      track.addEventListener('pointercancel', release, { passive: true });
      track.addEventListener('touchend', release, { passive: true });

      if (pauseBtn) {
        const label = () => {
          pauseBtn.textContent = isPaused ? 'Play' : 'Pause';
          pauseBtn.setAttribute('aria-label', isPaused ? 'Play slideshow' : 'Pause slideshow');
        };
        label();
        pauseBtn.addEventListener('click', () => {
          isPaused = !isPaused;
          label();
          if (isPaused) stop(); else start();
        });
      }

      hero.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { goToSlide(current - 1); restart(); }
        if (event.key === 'ArrowRight') { goToSlide(current + 1); restart(); }
      });
      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', restart);
      document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); else restart(); });
      hero.goToSlide = goToSlide;

      updateUI();
      start();
    });
  }

  /* ----------------------------------------------------
     2. Cart Drawer & AJAX Cart API
     The drawer's Liquid (sections/cart-drawer.liquid) is the only line template.
     Every add/change asks Shopify to render that section in the same response
     (Section Rendering API), so one round trip updates everything. Until the
     reply arrives the drawer is updated optimistically: added items appear at
     once, quantities and totals change on tap, removed lines collapse.
  ---------------------------------------------------- */
  const CART_SECTION = 'cart-drawer';
  const EASE = 'cubic-bezier(.22,1,.36,1)';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cartRoot = () => window.themeSettings?.cartRoot || '/';
  const cartRows = (key) => $$('.cart-line[data-key]').filter((row) => row.dataset.key === key && !row.dataset.removing);
  const drawerRows = () => $$('#cart-drawer-items .cart-line').filter((row) => !row.dataset.removing);

  function parseMoney(text) {
    const match = String(text || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    return match ? Math.round(parseFloat(match[1]) * 100) : 0;
  }

  // Count a money label from its current value to the new one.
  function tweenMoney(el, cents) {
    if (!el) return;
    const from = parseMoney(el.textContent);
    cancelAnimationFrame(el._tween);
    if (reduceMotion() || from === cents) { el.textContent = formatMoney(cents); return; }
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / 380);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = formatMoney(Math.round(from + (cents - from) * eased));
      if (t < 1) el._tween = requestAnimationFrame(step);
    };
    el._tween = requestAnimationFrame(step);
  }

  function animateIn(el) {
    if (reduceMotion() || !el.animate) return;
    const height = el.offsetHeight;
    el.style.overflow = 'hidden';
    el.animate([
      { height: '0px', opacity: 0, transform: 'translateY(-6px) scale(.98)' },
      { height: height + 'px', opacity: 1, transform: 'none' }
    ], { duration: 340, easing: EASE }).onfinish = () => { el.style.overflow = ''; };
  }

  function collapse(el) {
    el.dataset.removing = '1';
    if (reduceMotion() || !el.animate) { el.remove(); return; }
    const style = getComputedStyle(el);
    el.style.overflow = 'hidden';
    el.style.pointerEvents = 'none';
    el.animate([
      { height: el.offsetHeight + 'px', opacity: 1, transform: 'none', paddingTop: style.paddingTop, paddingBottom: style.paddingBottom },
      { height: '0px', opacity: 0, transform: 'translateX(28px)', paddingTop: '0px', paddingBottom: '0px' }
    ], { duration: 300, easing: EASE }).onfinish = () => el.remove();
  }

  function tick(el) {
    if (!el || reduceMotion() || !el.animate) return;
    el.animate([{ transform: 'translateY(-40%)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 220, easing: EASE });
  }

  // Image flies from the product into its line in the drawer.
  function flyToCart(sourceImg, row) {
    const targetBox = row?.querySelector('.cart-line-img');
    if (!sourceImg || !targetBox || reduceMotion() || !sourceImg.animate) return;
    const from = sourceImg.getBoundingClientRect();
    if (!from.width) return;
    const drawer = $('cart-drawer');
    const shift = drawer ? new DOMMatrixReadOnly(getComputedStyle(drawer).transform).m41 : 0;
    const to = targetBox.getBoundingClientRect();
    const clone = sourceImg.cloneNode();
    clone.removeAttribute('id');
    clone.removeAttribute('srcset');
    clone.src = sourceImg.currentSrc || sourceImg.src;
    clone.className = 'fly-img';
    Object.assign(clone.style, { left: from.left + 'px', top: from.top + 'px', width: from.width + 'px', height: from.height + 'px' });
    document.body.appendChild(clone);
    targetBox.style.opacity = '0';
    const dx = to.left - shift - from.left;
    const dy = to.top - from.top;
    const scale = Math.max(to.width / from.width, .08);
    clone.animate([
      { transform: 'none', borderRadius: '16px', opacity: 1 },
      { transform: `translate(${dx * .45}px, ${dy * .45 - 60}px) scale(${(1 + scale) / 2})`, borderRadius: '14px', opacity: 1, offset: .5 },
      { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, borderRadius: '12px', opacity: 1 }
    ], { duration: 560, easing: EASE }).onfinish = () => {
      clone.remove();
      const box = row.querySelector('.cart-line-img') || targetBox;
      box.style.opacity = '';
      box.animate?.([{ transform: 'scale(1.12)' }, { transform: 'none' }], { duration: 260, easing: EASE });
    };
  }

  // Title, image and price of the product whose form was submitted.
  function productInfo(form) {
    const card = form?.closest('.card');
    if (card) {
      return {
        title: card.querySelector('.card-t')?.textContent,
        img: card.querySelector('.card-media img'),
        price: card.querySelector('.price b')?.textContent
      };
    }
    const suggestion = form?.closest('.xsell-item');
    if (suggestion) {
      return {
        title: suggestion.querySelector('.xsell-title')?.textContent,
        img: suggestion.querySelector('.xsell-img img'),
        price: suggestion.querySelector('.xsell-price')?.textContent
      };
    }
    // getAttribute: the form has an input named "id", which shadows form.id.
    if (form?.getAttribute('id') === 'product-form') {
      const track = $('pdp-track');
      const active = track ? track.children[track._index || 0] : null;
      return {
        title: document.querySelector('.pdp-buy h1')?.textContent,
        img: active?.querySelector('img') || $('pdp-main'),
        price: document.querySelector('[data-pdp-price]')?.textContent
      };
    }
    return null;
  }

  // Add button: spinner while Shopify answers, a tick when it lands.
  function buttonState(btn, state) {
    if (!btn) return;
    if (!btn.querySelector('.btn-label')) btn.innerHTML = `<span class="btn-label">${btn.innerHTML}</span>`;
    clearTimeout(btn._stateTimer);
    btn.classList.remove('is-busy', 'is-added');
    if (state === 'busy') btn.classList.add('is-busy');
    if (state === 'added') {
      btn.classList.add('is-added');
      btn._stateTimer = setTimeout(() => btn.classList.remove('is-added'), 1400);
    }
  }

  // Automatic "pairs well with" for the product most recently added.
  // Shopify's complementary picks (Search & Discovery) first, then its
  // automatic related picks; the server-rendered size-matched list stays
  // if neither returns anything. Results are cached per product.
  const recCache = new Map();
  async function fetchRecs(productId) {
    const root = cartRoot();
    for (const intent of ['complementary', 'related']) {
      try {
        const res = await fetch(`${root}recommendations/products?product_id=${encodeURIComponent(productId)}&limit=10&section_id=cart-recs&intent=${intent}`);
        if (!res.ok) continue;
        const list = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('.xsell');
        if (list?.querySelector('.xsell-item')) return list.outerHTML;
      } catch (err) {
        console.warn('Cart suggestions:', err);
      }
    }
    return '';
  }
  async function loadAutoRecs() {
    const box = $('cart-drawer-recs');
    const anchor = box?.dataset.anchor;
    if (!box || !anchor) return;
    if (!recCache.has(anchor)) recCache.set(anchor, fetchRecs(anchor));
    const html = await recCache.get(anchor);
    if (!html || box.dataset.anchor !== anchor || !box.isConnected) return;
    const holder = document.createElement('div');
    holder.innerHTML = html;
    const inCart = new Set($$('#cart-drawer-items .cart-line[data-product]').map((row) => row.dataset.product));
    holder.querySelectorAll('.xsell-item').forEach((item) => { if (inCart.has(item.dataset.product)) item.remove(); });
    if (!holder.querySelector('.xsell-item')) return; // keep the fallback list
    const same = box.querySelector('.xsell')?.dataset.source === holder.firstElementChild.dataset.source
      && box.querySelectorAll('.xsell-item').length === holder.querySelectorAll('.xsell-item').length;
    if (same) return;
    box.innerHTML = holder.innerHTML;
    initImageFade(box);
    if (!reduceMotion()) box.querySelectorAll('.xsell-item').forEach((item, i) => item.animate([{ opacity: 0, transform: 'translateX(16px)' }, { opacity: 1, transform: 'none' }], { duration: 360, delay: i * 50, easing: EASE, fill: 'backwards' }));
  }

  const CartManager = {
    busy: Promise.resolve(),
    pending: new Map(),
    version: 0,

    openDrawer() {
      const drawer = $('cart-drawer');
      if (!drawer || drawer.classList.contains('is-open')) return;
      this.lastFocus = document.activeElement;
      drawer.classList.add('is-open', 'is-opening');
      clearTimeout(this.openingTimer);
      this.openingTimer = setTimeout(() => drawer.classList.remove('is-opening'), 800);
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      $('scrim')?.classList.add('is-open');
      requestAnimationFrame(() => drawer.querySelector('.panel-x')?.focus({ preventScroll: true }));
      loadAutoRecs();
    },
    closeDrawer() {
      const drawer = $('cart-drawer');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      if (!$('menu')?.classList.contains('is-open')) $('scrim')?.classList.remove('is-open');
      if (this.lastFocus?.focus) this.lastFocus.focus({ preventScroll: true });
    },
    updateBadges(count) {
      $$('[data-cart-count]').forEach((el) => {
        const changed = el.textContent !== String(count);
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
        if (changed && !reduceMotion()) {
          el.classList.remove('pop'); void el.offsetWidth; el.classList.add('pop');
          const btn = el.closest('.btn-cart, #tab-cart');
          if (btn) { btn.classList.remove('nudge'); void btn.offsetWidth; btn.classList.add('nudge'); }
        }
      });
      const label = $('cart-n');
      if (label) label.textContent = `${count} item${count === 1 ? '' : 's'}`;
      const drawer = $('cart-drawer');
      if (drawer) drawer.dataset.count = count;
    },
    announce(message) {
      const status = $('cart-status');
      if (status) status.textContent = message;
    },

    sectionIds() {
      const ids = [CART_SECTION];
      const pageId = document.querySelector('[data-cart-page]')?.dataset.cartSectionId;
      if (pageId) ids.push(pageId);
      return ids;
    },
    async post(path, body) {
      const res = await fetch(`${path}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Cart request failed');
      return data;
    },

    // Bring the drawer (and cart page) in line with Shopify's rendered HTML.
    applySections(sections) {
      if (!sections) return;
      const html = sections[CART_SECTION];
      if (html) this.applyDrawer(html);
      const pageId = document.querySelector('[data-cart-page]')?.dataset.cartSectionId;
      if (pageId && sections[pageId]) this.applyCartPage(sections[pageId]);
    },
    applyDrawer(html) {
      const next = new DOMParser().parseFromString(html, 'text/html');
      const nextDrawer = next.getElementById('cart-drawer');
      const list = $('cart-drawer-items');
      if (!nextDrawer || !list) return;

      // Shopify has answered: the opening stagger must not replay on rows that move.
      $('cart-drawer')?.classList.remove('is-opening');
      const current = new Map(drawerRows().filter((row) => row.dataset.key).map((row) => [row.dataset.key, row]));
      const optimistic = $$('.cart-line.is-optimistic', list);
      const ordered = [];
      [...next.getElementById('cart-drawer-items').children].forEach((incoming) => {
        let row = current.get(incoming.dataset.key);
        if (row) current.delete(incoming.dataset.key);
        else row = optimistic.find((o) => o.dataset.variant === incoming.dataset.variant && !o.dataset.claimed);
        if (row) {
          row.dataset.claimed = '1';
          const priceBefore = row.querySelector('.cart-line-price')?.textContent;
          row.className = incoming.className;
          [...incoming.attributes].forEach((attr) => row.setAttribute(attr.name, attr.value));
          row.removeAttribute('data-claimed');
          const oldBox = row.querySelector('.cart-line-img');
          const oldImg = oldBox?.querySelector('img');
          const flying = oldBox?.style.opacity;
          row.innerHTML = incoming.innerHTML;
          // Keep the image that is already on screen (no blank flash); the
          // server's thumbnail replaces it only once it has loaded.
          const newBox = row.querySelector('.cart-line-img');
          const fresh = newBox?.querySelector('img');
          if (oldImg && oldImg.complete && oldImg.naturalWidth && fresh && !newBox.classList.contains('cz-thumb')) {
            const nextSrc = fresh.getAttribute('src');
            fresh.replaceWith(oldImg);
            if (nextSrc && oldImg.getAttribute('src') !== nextSrc) {
              const pre = new Image();
              pre.onload = () => { oldImg.removeAttribute('srcset'); oldImg.src = nextSrc; };
              pre.src = nextSrc;
            }
          }
          if (flying && newBox) newBox.style.opacity = flying;
          if (priceBefore && priceBefore !== row.querySelector('.cart-line-price')?.textContent) tick(row.querySelector('.cart-line-price'));
          ordered.push(row);
        } else {
          const adopted = document.importNode(incoming, true);
          adopted.dataset.enter = '1';
          ordered.push(adopted);
        }
      });
      optimistic.filter((o) => !ordered.includes(o)).forEach(collapse);
      current.forEach(collapse);
      ordered.forEach((row, index) => {
        const at = list.children[index];
        if (at !== row) list.insertBefore(row, at || null);
        if (row.dataset.enter) { delete row.dataset.enter; animateIn(row); }
      });

      const footer = $('cart-drawer-footer');
      const nextFooter = next.getElementById('cart-drawer-footer');
      if (footer && nextFooter) {
        const totals = ['cart-drawer-subtotal'].map((id) => [id, footer.querySelector('#' + id)?.textContent]);
        footer.innerHTML = nextFooter.innerHTML;
        footer.style.display = nextFooter.style.display;
        initImageFade(footer);
        totals.forEach(([id, before]) => {
          const el = $(id);
          if (el && before && before !== el.textContent) { const target = parseMoney(el.textContent); el.textContent = before; tweenMoney(el, target); }
        });
      }
      const recs = $('cart-drawer-recs');
      const nextRecs = next.getElementById('cart-drawer-recs');
      if (recs && nextRecs) {
        const anchorChanged = recs.dataset.anchor !== nextRecs.dataset.anchor;
        recs.dataset.anchor = nextRecs.dataset.anchor;
        // Keep automatic suggestions on screen; only the fallback list is re-rendered.
        if (anchorChanged || !recs.querySelector('.xsell[data-source]')) { recs.innerHTML = nextRecs.innerHTML; initImageFade(recs); }
        loadAutoRecs();
      }
      const empty = $('cart-drawer-empty');
      const nextEmpty = next.getElementById('cart-drawer-empty');
      if (empty && nextEmpty) this.toggleEmpty(nextEmpty.style.display !== 'none');
      this.updateBadges(Number(nextDrawer.dataset.count) || 0);
    },
    applyCartPage(html) {
      const currentRoot = document.querySelector('[data-cart-page]');
      const nextRoot = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-page]');
      if (!currentRoot || !nextRoot) return;
      const scrollTop = window.scrollY;
      currentRoot.replaceWith(nextRoot);
      window.scrollTo(0, scrollTop);
      initThemeMotion(nextRoot);
    },
    toggleEmpty(isEmpty) {
      const empty = $('cart-drawer-empty');
      const footer = $('cart-drawer-footer');
      if (empty) {
        const wasHidden = empty.style.display === 'none';
        empty.style.display = isEmpty ? 'block' : 'none';
        if (isEmpty && wasHidden && !reduceMotion()) empty.animate?.([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 300, easing: EASE });
      }
      if (footer) footer.style.display = isEmpty ? 'none' : '';
      const recs = $('cart-drawer-recs');
      if (recs) recs.hidden = isEmpty;
      const proof = document.querySelector('#cart-drawer .cd-proof');
      if (proof) proof.hidden = isEmpty;
    },

    // Totals from what is on screen, before Shopify confirms them.
    estimateTotals() {
      let count = 0; let total = 0;
      drawerRows().forEach((row) => {
        const qty = Number(row.querySelector('.qty-num')?.textContent) || 0;
        count += qty;
        total += (Number(row.dataset.unit) || 0) * qty;
      });
      this.updateBadges(count);
      this.toggleEmpty(count === 0);
      tweenMoney($('cart-drawer-subtotal'), total);
      tweenMoney(document.querySelector('#cart-page-aside .pd-total b'), total);
    },

    async refreshCart() {
      try {
        const url = new URL(cartRoot(), window.location.origin);
        url.searchParams.set('sections', this.sectionIds().join(','));
        const res = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`Cart refresh failed (${res.status})`);
        const sections = await res.json();
        this.applySections(sections);
        return { item_count: Number($('cart-drawer')?.dataset.count) || 0 };
      } catch (err) {
        console.warn('Cart refresh:', err);
        return null;
      }
    },

    showOptimisticAdd(variantId, quantity, info) {
      const list = $('cart-drawer-items');
      if (!list) return;
      const existing = drawerRows().find((row) => row.dataset.variant === String(variantId) && !row.querySelector('.cz-props'));
      if (existing) {
        const num = existing.querySelector('.qty-num');
        if (num) { num.textContent = (Number(num.textContent) || 0) + quantity; tick(num); }
        const price = existing.querySelector('.cart-line-price');
        if (price) tweenMoney(price, (Number(existing.dataset.unit) || 0) * Number(num?.textContent || quantity));
        list.insertBefore(existing, list.firstChild);
        existing.classList.add('is-optimistic');
        this.estimateTotals();
        flyToCart(info?.img, existing);
        return;
      }
      if (!info?.title) return;
      const unit = parseMoney(info.price);
      const row = document.createElement('div');
      row.className = 'cart-line is-optimistic';
      row.dataset.variant = String(variantId);
      row.dataset.unit = String(unit);
      const imgSrc = info.img ? (info.img.currentSrc || info.img.src) : '';
      row.innerHTML = `
        <div class="cart-line-img">${imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="" width="72" height="72">` : ''}</div>
        <div class="cart-line-info">
          <h4 class="cart-line-title">${escapeHtml(info.title.trim())}</h4>
          <div class="cart-line-price">${formatMoney(unit * quantity)}</div>
          <div class="cart-line-ctrls"><div class="qty qty-sm"><span class="qty-num">${quantity}</span></div></div>
        </div>`;
      list.insertBefore(row, list.firstChild);
      this.toggleEmpty(false);
      animateIn(row);
      this.estimateTotals();
      flyToCart(info.img, row);
    },

    async addToCart(request, form, button) {
      const isCartPage = document.body.dataset.page === 'cart';
      buttonState(button, 'busy');
      const single = request && !Array.isArray(request) ? request : null;
      if (!isCartPage) {
        this.openDrawer();
        if (single?.id && !single.properties) this.showOptimisticAdd(single.id, Number(single.quantity) || 1, productInfo(form));
      }
      const version = ++this.version;
      const run = (async () => {
        try {
          const data = await this.post(window.themeSettings?.cartAddUrl || '/cart/add', Array.isArray(request) ? { items: request } : { ...request });
          // A newer add/change is queued behind this one and will apply its own render.
          if (version === this.version) this.applySections(data.sections);
          this.announce('Added to your cart');
          buttonState(button, 'added');
          return data;
        } catch (err) {
          buttonState(button, null);
          console.warn('Add to cart:', err);
          showToast(err.message && err.message !== 'Cart request failed' ? err.message : 'Could not add this item. Please try again.');
          await this.refreshCart();
          return null;
        }
      })();
      this.busy = Promise.all([this.busy, run]);
      return run;
    },

    // Quantity changes paint at once; taps on the same line collapse into one
    // request, and requests run one at a time, targeted by line-item key.
    changeQuantity(keyOrLine, qty) {
      let key = String(keyOrLine);
      if (/^\d+$/.test(key)) {
        const row = document.querySelector(`#cart-drawer-items .cart-line[data-line="${key}"]`);
        if (row?.dataset.key) key = row.dataset.key;
      }
      qty = Math.max(0, Number(qty) || 0);
      this.paintQuantity(key, qty);
      const version = ++this.version;
      const entry = this.pending.get(key) || {};
      entry.qty = qty;
      clearTimeout(entry.timer);
      this.pending.set(key, entry);
      return new Promise((resolve) => {
        entry.timer = setTimeout(() => {
          this.pending.delete(key);
          const run = this.busy.then(async () => {
            try {
              const body = /^\d+$/.test(key) ? { line: Number(key), quantity: qty } : { id: key, quantity: qty };
              const data = await this.post(window.themeSettings?.cartChangeUrl || '/cart/change', body);
              if (version === this.version && !this.pending.size) this.applySections(data.sections);
            } catch (err) {
              console.warn('Cart update:', err);
              showToast(err.message && err.message !== 'Cart request failed' ? err.message : 'Could not update your cart. Please try again.');
              await this.refreshCart();
            }
          });
          this.busy = run;
          run.then(resolve);
        }, qty === 0 ? 0 : 220);
      });
    },
    paintQuantity(key, qty) {
      cartRows(key).forEach((row) => {
        if (qty === 0) { collapse(row); return; }
        const num = row.querySelector('.qty-num');
        if (num && num.textContent !== String(qty)) { num.textContent = qty; tick(num); }
        tweenMoney(row.querySelector('.cart-line-price'), (Number(row.dataset.unit) || 0) * qty);
        row.querySelectorAll('.qty-btn[data-cart-qty]').forEach((btn) => {
          const up = btn.textContent.trim() === '+';
          btn.dataset.cartQty = String(up ? qty + 1 : Math.max(0, qty - 1));
        });
      });
      this.estimateTotals();
      this.announce(qty === 0 ? 'Item removed' : `Quantity updated to ${qty}`);
    },
    // Send any queued quantity taps right away (used before checkout).
    flush() {
      this.pending.forEach((entry) => { clearTimeout(entry.timer); });
      const keys = [...this.pending.entries()];
      this.pending.clear();
      keys.forEach(([key, entry]) => {
        this.busy = this.busy.then(() => this.post(window.themeSettings?.cartChangeUrl || '/cart/change', { id: key, quantity: entry.qty }).catch((err) => console.warn('Cart flush:', err)));
      });
      return this.busy;
    }
  };
  window.ShopShipCart = CartManager;

  /* ----------------------------------------------------
     3. Global Event Delegation
  ---------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    initHeroSliders();
    initThemeMotion();
    initPredictiveSearch();
    initProductRecommendations();
    initStickyBar();
    initReviewWriter();
    initReviewFilters();

    // Drawer triggers
    $$('[data-cart-trigger], .btn-cart').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        CartManager.openDrawer();
      });
    });

    $$('[data-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', () => CartManager.closeDrawer());
    });

    // Handle AJAX Add-to-cart forms
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('.ajax-cart-form');
      if (form) {
        if (e.submitter && e.submitter.name === 'checkout') return;
        e.preventDefault();
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && !variantInput.value && form.dataset.variantId) {
          variantInput.value = form.dataset.variantId;
          variantInput.setAttribute('value', form.dataset.variantId);
        }
        const formData = new FormData(form);
        const id = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1', 10);
        const button = e.submitter || form.querySelector('[type="submit"]');
        CartManager.addToCart({ id, quantity }, form, button);
      }
    });

    // Quantity changes and removals (drawer and cart page), keyed by line item
    document.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('[data-cart-qty]');
      const removeLink = e.target.closest('[data-cart-page] a.cart-line-del');
      const row = (qtyBtn || removeLink)?.closest('.cart-line');
      if (qtyBtn) {
        // Count from the number on screen: it may already include taps (or an
        // add) that Shopify has not confirmed yet.
        const shown = Number(row?.querySelector('.qty-num')?.textContent);
        let qty = Number(qtyBtn.dataset.cartQty);
        if (!qtyBtn.classList.contains('cart-line-del') && Number.isFinite(shown) && shown > 0) {
          qty = shown + (qtyBtn.textContent.trim() === '+' ? 1 : -1);
        }
        CartManager.changeQuantity(row?.dataset.key || qtyBtn.dataset.line, qty);
      } else if (removeLink && row?.dataset.key) {
        e.preventDefault();
        CartManager.changeQuantity(row.dataset.key, 0);
      }
    });

    // Checkout waits for any add or quantity change still on its way, so the
    // order always matches what the shopper sees.
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.matches('#cart-drawer-form, [data-cart-page] form[action="/checkout"]') || form.dataset.ready) return;
      e.preventDefault();
      const btn = form.querySelector('[name="checkout"]');
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
      const label = btn?.querySelector('.btn-label');
      if (label) label.textContent = 'Opening secure checkout…';
      // Straight to Shopify's checkout (GET) once pending cart updates land:
      // skips the cart form POST + redirect round trip.
      CartManager.flush().then(() => {
        form.dataset.ready = '1';
        window.location.href = `${cartRoot()}checkout`;
      });
    }, true);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && $('cart-drawer')?.classList.contains('is-open')) CartManager.closeDrawer();
    });

    // Back from checkout (page restored from the back/forward cache): reset the button.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      $$('#cart-drawer-form, [data-cart-page] form[action="/checkout"]').forEach((form) => {
        delete form.dataset.ready;
        const btn = form.querySelector('[name="checkout"]');
        if (!btn) return;
        btn.disabled = false;
        btn.classList.remove('is-loading');
        const label = btn.querySelector('.btn-label');
        if (label) label.textContent = 'Checkout securely';
      });
    });

    // FAQ Accordion
    $$('.faq-item').forEach((item) => {
      const btn = item.querySelector('.faq-q');
      if (btn) {
        btn.addEventListener('click', () => {
          const isOpen = item.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      }
    });

    // Combo Bundle Builder
    initBundleBuilder();

    // Product Page Gallery & Actions
    initProductPage();

    // Pincode Checker

    // Bulk WhatsApp form
  });

  // Lazy images that are still downloading fade in when they arrive instead
  // of popping in. Images already loaded are never touched.
  function initImageFade(root = document) {
    root.querySelectorAll('.card-media img[loading="lazy"], .xsell-img img, .rr-pic').forEach((img) => {
      if (img.complete || img.dataset.fade) return;
      img.dataset.fade = '1';
      img.classList.add('img-fade');
      const done = () => img.classList.add('is-loaded');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  // "Write a review" opens Judge.me's own form (its widget stays on the page,
  // hidden, inside the reviews section) so reviews keep flowing to Judge.me.
  function initReviewWriter() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-rr-write]');
      if (!trigger) return;
      event.preventDefault();
      const started = Date.now();
      const open = () => {
        const button = [...document.querySelectorAll('.rr-engine button')].find((b) => /write a review/i.test(b.textContent));
        if (button) { trigger.classList.remove('is-busy'); button.click(); return; }
        if (Date.now() - started < 6000) { trigger.classList.add('is-busy'); setTimeout(open, 250); return; }
        trigger.classList.remove('is-busy');
        showToast('Reviews are still loading. Please try again in a moment.');
      };
      open();
    });
  }

  // Reviews page filters (All / With photos / 5 stars / 4 and below).
  function initReviewFilters() {
    const bar = document.querySelector('[data-rv-filters]');
    if (!bar) return;
    bar.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rv-filter]');
      if (!button) return;
      bar.querySelectorAll('[data-rv-filter]').forEach((b) => b.classList.toggle('is-active', b === button));
      const filter = button.dataset.rvFilter;
      let delay = 0;
      document.querySelectorAll('.rr-rail--grid .rr-card').forEach((card) => {
        const rating = Number(card.dataset.rating);
        const show = filter === 'all' || (filter === 'photos' && card.dataset.photos === '1') || (filter === '5' && rating === 5) || (filter === 'lower' && rating < 5);
        const wasHidden = card.hidden;
        card.hidden = !show;
        if (show && !reduceMotion() && card.animate) {
          card.animate([{ opacity: 0, transform: 'translateY(10px) scale(.98)' }, { opacity: 1, transform: 'none' }], { duration: 320, delay: wasHidden ? delay : 0, easing: EASE, fill: 'backwards' });
          delay += 30;
        }
      });
    });
  }

  // Sticky category bar gets a surface once it is pinned.
  function initStickyBar() {
    const bar = document.querySelector('[data-plp-bar]');
    if (!bar || !('IntersectionObserver' in window)) return;
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    bar.before(sentinel);
    new IntersectionObserver(([entry]) => bar.classList.toggle('is-stuck', !entry.isIntersecting)).observe(sentinel);
  }

  function initThemeMotion(root = document) {
    initImageFade(root);
    root.querySelectorAll('[data-rail]').forEach((button) => {
      if (button.dataset.wired) return;
      button.dataset.wired = 'true';
      button.addEventListener('click', () => {
        const rail = document.getElementById(button.dataset.rail);
        if (rail) rail.scrollBy({ left: (Number(button.dataset.direction) || 1) * rail.clientWidth * .85, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      });
    });
    const header = document.querySelector('.hdr');
    if (header && !header.dataset.scrollWired) {
      header.dataset.scrollWired = 'true';
      const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    const toTop = $('to-top');
    if (toTop && !toTop.dataset.scrollWired) {
      toTop.dataset.scrollWired = 'true';
      toTop.addEventListener('click', () => {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        window.scrollTo({ top: 0, behavior });
      });
    }
    // Section reveals are pure CSS (scroll-driven, see premium.css): content is
    // never hidden waiting for JavaScript.
  }

  function initPredictiveSearch() {
    function wire(inputId, dropId, typeId) {
      const input = $(inputId);
      const drop = $(dropId);
      const form = input?.form;
      const typeSelect = typeId ? $(typeId) : null;
      if (!input || !drop || !form) return;
      let timer;
      let controller;
      const close = () => {
        drop.removeAttribute('data-open');
        drop.replaceChildren();
        input.setAttribute('aria-expanded', 'false');
      };
      const render = (items, query) => {
        drop.replaceChildren();
        const heading = document.createElement('div');
        heading.className = 'srch-hd';
        heading.textContent = items.length ? 'Products' : 'No products found for “' + query + '”';
        drop.appendChild(heading);
        items.forEach((item) => {
          const link = document.createElement('a');
          link.className = 'sug';
          link.setAttribute('role', 'option');
          link.href = item.url || '/search';
          const imageUrl = item.featured_image?.url || item.featured_image || item.image;
          if (imageUrl) {
            const image = document.createElement('img');
            const parsedImage = new URL(imageUrl, window.location.origin);
            parsedImage.searchParams.set('width', '96');
            image.src = parsedImage.toString();
            image.alt = '';
            image.width = 48;
            image.height = 48;
            image.loading = 'lazy';
            link.appendChild(image);
          }
          const text = document.createElement('span');
          const title = document.createElement('b');
          title.textContent = item.title || 'Product';
          text.appendChild(title);
          const metaText = item.product_type || item.vendor;
          if (metaText) {
            const meta = document.createElement('small');
            meta.textContent = metaText;
            text.appendChild(meta);
          }
          link.appendChild(text);
          if (item.price || item.formatted_price) {
            const price = document.createElement('strong');
            const amount = Number(item.price_min ?? item.price);
            const formattedAmount = Number.isFinite(amount)
              ? '₹' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
              : '';
            price.textContent = item.formatted_price || formattedAmount;
            link.appendChild(price);
          }
          drop.appendChild(link);
        });
        drop.setAttribute('data-open', '');
        input.setAttribute('aria-expanded', 'true');
      };
      const search = async () => {
        const query = input.value.trim();
        if (!query) { close(); return; }
        if (controller) controller.abort();
        controller = new AbortController();
        const searchType = typeSelect?.value || 'product';
        const endpoint = new URL(form.action.replace(/\/$/, '') + '/suggest.json', window.location.origin);
        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set('resources[type]', searchType);
        endpoint.searchParams.set('resources[limit]', '6');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
        endpoint.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
        try {
          const response = await fetch(endpoint.toString(), { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Predictive search failed');
          const result = await response.json();
          const resources = result.resources?.results || {};
          const key = searchType === 'product' ? 'products' : searchType === 'page' ? 'pages' : 'articles';
          render(resources[key] || [], query);
        } catch (error) {
          if (error.name !== 'AbortError') close();
        }
      };
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(search, 160);
      });
      input.addEventListener('focus', () => { if (input.value.trim()) search(); });
      typeSelect?.addEventListener('change', search);
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        if (event.key === 'ArrowDown') {
          const firstResult = drop.querySelector('a[role="option"]');
          if (firstResult) { event.preventDefault(); firstResult.focus(); }
        }
      });
      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) close();
      });
    }
    wire('q', 'srch-drop', 'cat');
    wire('q-m', 'srch-drop-m');
  }

  document.addEventListener('shopify:section:load', (event) => {
    initHeroSliders();
    initThemeMotion(event.target);
    initProductRecommendations(event.target);
  });

  function initProductRecommendations(root = document) {
    const section = root.querySelector('[data-recommendations-url]');
    if (!section || section.dataset.recommendationsWired) return;
    section.dataset.recommendationsWired = 'true';

    const load = async () => {
      try {
        const response = await fetch(section.dataset.recommendationsUrl, { cache: 'force-cache' });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const recommendedSection = html.querySelector('#pdp-related-sec');
        const recommendedRail = recommendedSection?.querySelector('#pdp-related');
        const currentRail = section.querySelector('#pdp-related');
        if (!currentRail || !recommendedRail?.querySelector('.card')) return;
        currentRail.replaceChildren(...[...recommendedRail.children].map((item) => document.importNode(item, true)));
        initImageFade(currentRail);
        const currentHeading = section.querySelector('#pdp-related-title');
        const recommendedHeading = recommendedSection.querySelector('#pdp-related-title');
        if (currentHeading && recommendedHeading) currentHeading.textContent = recommendedHeading.textContent;
      } catch (error) {
        console.warn('Product recommendations:', error);
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        load();
      }, { rootMargin: '0px 0px 250px 0px' });
      observer.observe(section);
    } else {
      load();
    }
  }
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('.hero-slide');
    if (!slide) return;
    const hero = slide.closest('.hero-main');
    const slides = [...hero.querySelectorAll('.hero-slide')];
    if (hero.goToSlide) hero.goToSlide(slides.indexOf(slide));
  });

  /* ----------------------------------------------------
     4. Combo Bundle Builder Section
  ---------------------------------------------------- */
  function initBundleBuilder() {
    const bundleSection = document.querySelector('.bundle-sec');
    if (!bundleSection) return;

    const addBtn = bundleSection.querySelector('#bundle-add');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        const items = [];
        bundleSection.querySelectorAll('[data-bundle-item]').forEach((item) => {
          const id = item.dataset.bundleItem;
          if (id) items.push({ id, quantity: 1 });
        });

        if (items.length > 0) {
          addBtn.disabled = true;
          addBtn.textContent = 'Adding both…';
          CartManager.addToCart(items).finally(() => {
            setTimeout(() => {
              addBtn.disabled = false;
              addBtn.textContent = 'Add both to cart';
            }, 800);
          });
        } else {
          showToast('Added bundle to cart');
          CartManager.openDrawer();
        }
      });
    }
  }

  /* ----------------------------------------------------
     5. Product Page Gallery & Quantity
  ---------------------------------------------------- */
  function initProductPage() {
    const productForm = $('product-form');
    let variantInput = null;
    let optionSelects = [];
    let productVariants = [];
    let updateSelectedVariant = null;
    let showVariantImage = null;
    let selectedVariant = null;
    if (productForm) {
      variantInput = productForm.querySelector('input[name="id"]');
      optionSelects = [...productForm.querySelectorAll('.variant-select')];
      try { productVariants = JSON.parse(productForm.dataset.variants || '[]'); } catch (error) { productVariants = []; }
      const requestedVariantId = new URL(window.location.href).searchParams.get('variant');
      const requestedVariant = requestedVariantId && productVariants.find((candidate) => String(candidate.id) === requestedVariantId);
      if (requestedVariant) {
        optionSelects.forEach((select, index) => {
          const requestedOption = requestedVariant[`option${index + 1}`];
          if (requestedOption !== undefined && [...select.options].some((option) => option.value === requestedOption)) {
            select.value = requestedOption;
          }
        });
      }
      const addButton = $('pdp-add');
      const buyButton = $('pdp-buy');
      updateSelectedVariant = () => {
        const selected = optionSelects.map((select) => select.value);
        const variant = productVariants.find((candidate) => selected.every((value, index) => candidate[`option${index + 1}`] === value));
        if (!variant) {
          if (variantInput) variantInput.value = '';
          if (addButton) { addButton.disabled = true; addButton.textContent = 'Unavailable'; }
          if (buyButton) buyButton.disabled = true;
          return;
        }
        selectedVariant = variant;
        if (variantInput) {
          variantInput.value = String(variant.id);
          variantInput.setAttribute('value', String(variant.id));
        }
        const price = Number(variant.price) || 0;
        const compare = Number(variant.compare_at_price) || 0;
        const isSale = compare > price;
        const priceEl = document.querySelector('[data-pdp-price]');
        const compareEl = document.querySelector('[data-pdp-compare]');
        const discountEl = document.querySelector('[data-pdp-discount]');
        const savingsEl = document.querySelector('[data-pdp-savings]');
        if (priceEl) priceEl.textContent = formatMoney(price);
        if (compareEl) { compareEl.hidden = !isSale; compareEl.textContent = formatMoney(compare); }
        if (discountEl) {
          const percentage = isSale ? Math.round(((compare - price) * 100) / compare) : 0;
          discountEl.hidden = !isSale;
          discountEl.textContent = `${percentage}% off`;
        }
        if (savingsEl) { savingsEl.hidden = !isSale; savingsEl.textContent = isSale ? `You save ${formatMoney(compare - price)}` : ''; }
        const couponAmount = Math.round(price * (Number(window.themeSettings?.couponPercent) || 10) / 100);
        const couponEl = document.querySelector('[data-pdp-coupon]');
        if (couponEl) couponEl.textContent = `${window.themeSettings?.defaultCoupon || 'WELCOME10'} takes another ${formatMoney(couponAmount)} off at checkout`;
        if (addButton) { addButton.disabled = !variant.available; addButton.textContent = variant.available ? 'Add to cart' : 'Sold out'; }
        const stockLine = document.querySelector('.pdp-stock');
        if (stockLine) stockLine.hidden = !variant.available;
        if (buyButton) buyButton.disabled = !variant.available;
        document.querySelectorAll('.pdp-sticky-copy span').forEach((node) => { node.textContent = formatMoney(price); });
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (showVariantImage) showVariantImage(variant);
      };
      optionSelects.forEach((select) => select.addEventListener('change', updateSelectedVariant));
      productForm.addEventListener('submit', () => {
        const selected = optionSelects.map((select) => select.value);
        const variant = productVariants.find((candidate) => selected.every((value, index) => candidate[`option${index + 1}`] === value)) || selectedVariant;
        const liveVariantInput = productForm.querySelector('input[name="id"]');
        if (liveVariantInput && variant) {
          liveVariantInput.value = String(variant.id);
          liveVariantInput.setAttribute('value', String(variant.id));
        }
      }, true);
      if (productVariants.length && !optionSelects.length) {
        selectedVariant = productVariants.find((variant) => String(variant.id) === variantInput?.value) || productVariants[0];
        if (variantInput && selectedVariant) variantInput.value = selectedVariant.id;
      }
    }

    // Gallery: slides live in a native scroll-snap track, so swiping is the
    // browser's own momentum scroll. JS only keeps dots/thumbs in sync and
    // drives arrows, thumbnails, keyboard and variant changes.
    const track = $('pdp-track');
    if (track) {
      const gallery = track.closest('[data-gallery]');
      const slides = [...track.querySelectorAll('.pdp-slide')];
      const thumbs = [...gallery.querySelectorAll('[data-goto]')];
      const thumbRail = $('pdp-thumbs');
      const dots = [...gallery.querySelectorAll('.pdp-dots i')];
      track._index = 0;

      // Keep the neighbouring slides loaded so a swipe never shows a blank.
      const warm = (index) => {
        [index - 1, index + 1].forEach((i) => {
          const img = slides[(i + slides.length) % slides.length]?.querySelector('img[loading="lazy"]');
          if (img) img.loading = 'eager';
        });
      };
      if (slides.length > 1) {
        if ('requestIdleCallback' in window) requestIdleCallback(() => warm(0), { timeout: 2000 });
        else setTimeout(() => warm(0), 1200);
      }

      const setActive = (index) => {
        if (index === track._index || index < 0) return;
        track._index = index;
        warm(index);
        thumbs.forEach((thumb, i) => (i === index ? thumb.setAttribute('aria-current', 'true') : thumb.removeAttribute('aria-current')));
        dots.forEach((dot, i) => dot.classList.toggle('is-on', i === index));
        const thumb = thumbs[index];
        if (thumb && thumbRail) {
          const li = thumb.parentElement;
          thumbRail.scrollTo({ left: li.offsetLeft - (thumbRail.clientWidth - li.offsetWidth) / 2, behavior: reduceMotion() ? 'auto' : 'smooth' });
        }
      };
      const goTo = (index) => {
        if (!slides.length) return;
        const target = (index + slides.length) % slides.length;
        track.scrollTo({ left: slides[target].offsetLeft - slides[0].offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
        setActive(target);
      };

      if (slides.length > 1 && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { if (entry.isIntersecting) setActive(slides.indexOf(entry.target)); });
        }, { root: track, threshold: .6 });
        slides.forEach((slide) => observer.observe(slide));
      }
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.goto))));
      gallery.querySelectorAll('[data-pdp-step]').forEach((button) => {
        button.addEventListener('click', () => goTo(track._index + Number(button.dataset.pdpStep)));
      });
      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(track._index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); goTo(track._index + 1); }
      });

      // Desktop hover zoom follows the pointer.
      const figure = track.closest('.pdp-shot');
      if (figure?.dataset.hoverZoom === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        track.addEventListener('pointermove', (event) => {
          const img = event.target.closest('.pdp-slide')?.querySelector('img');
          if (!img) return;
          const rect = img.getBoundingClientRect();
          img.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
          img.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
        });
      }

      showVariantImage = (variant) => {
        const image = variant.featured_image || variant.featured_media?.preview_image;
        let index = -1;
        if (image) {
          const id = String(variant.featured_image?.id || '');
          let path = '';
          try { path = new URL(image.src || image, window.location.origin).pathname; } catch (error) { path = ''; }
          index = slides.findIndex((slide) => (id && slide.dataset.mediaId === id) || (path && slide.dataset.src && new URL(slide.dataset.src, window.location.origin).pathname === path));
        }
        if (index < 0) {
          // No variant image: match the option name against image alt text.
          const terms = [variant.title, ...(variant.options || [])].join(' ').toLowerCase().split(/\s+/).map((t) => t.replace(/[^a-z0-9]/g, '')).filter((t) => t.length > 1 && !['default', 'title', 'both', 'mould', 'moulds', 'option', 'mm', 'cm'].includes(t));
          if (terms.length) index = slides.findIndex((slide) => { const alt = (slide.querySelector('img')?.alt || '').toLowerCase().replace(/[^a-z0-9]/g, ''); return terms.some((t) => alt.includes(t)); });
        }
        if (index >= 0) goTo(index);
      };
      if (updateSelectedVariant && optionSelects.length) updateSelectedVariant();
    }

    // PDP Quantity buttons
    $$('.pdp-qty [data-pq]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const display = $('pdp-qty');
        const input = $('Quantity');
        const diff = Number(btn.dataset.pq);
        let curr = parseInt(input ? input.value : (display ? display.textContent : '1'), 10) || 1;
        curr = Math.min(10, Math.max(1, curr + diff));
        if (display) display.textContent = curr;
        if (input) input.value = curr;
      });
    });

    const stickyCart = document.querySelector('[data-pdp-sticky]');
    const primaryAdd = $('pdp-add');
    const stickyAdd = document.querySelector('[data-pdp-sticky-add]');
    if (stickyCart && primaryAdd && stickyAdd && 'IntersectionObserver' in window) {
      let hideStickyTimer;
      const observer = new IntersectionObserver(([entry]) => {
        clearTimeout(hideStickyTimer);
        if (entry.isIntersecting) {
          stickyCart.classList.remove('is-visible');
          hideStickyTimer = setTimeout(() => { stickyCart.hidden = true; }, 300);
        } else {
          stickyCart.hidden = false;
          requestAnimationFrame(() => stickyCart.classList.add('is-visible'));
        }
      }, { threshold: .1 });
      observer.observe(primaryAdd);
      stickyAdd.addEventListener('click', () => primaryAdd.click());
    }
  }


})();
